/**
 * API Client - Core
 *
 * Centralized API client with authentication, error handling, and token management.
 * Feature-specific API modules live in their respective feature directories.
 *
 * This file exports:
 * - Token management (setAuthToken, getAuthToken, setUnauthorizedHandler)
 * - Low-level helpers (apiRequest, handleResponse, getAuthHeaders, API_BASE_URL)
 * - Generic apiClient object (get/post/put/patch/delete)
 * - Re-exports from all feature API modules for backward compatibility
 */
import {
  getActiveStudentId,
  getDeviceToken,
  removePairedStudent,
} from '@/features/student-auth/deviceAuth';

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';

// Token management
let authToken: string | null = null;

export function setAuthToken(token: string | null) {
  authToken = token;
}

export function getAuthToken(): string | null {
  return authToken;
}

export function getAuthHeaders(): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  // Prefer a Device token when an active student is signed in on this device,
  // otherwise fall back to the parent JWT.
  const activeStudent = getActiveStudentId();
  if (activeStudent) {
    const deviceToken = getDeviceToken(activeStudent);
    if (deviceToken) {
      headers['Authorization'] = `Device ${deviceToken}`;
      return headers;
    }
  }

  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }

  return headers;
}

// Callback for handling unauthorized responses (401)
let onUnauthorized: (() => void) | null = null;

export function setUnauthorizedHandler(handler: () => void) {
  onUnauthorized = handler;
}

export async function handleResponse<T>(response: Response, defaultMessage: string): Promise<T> {
  if (response.ok) {
    if (response.status === 204) return null as T;
    return response.json();
  }

  // Handle 401 Unauthorized
  if (response.status === 401) {
    // A revoked or unknown device token: drop the paired student and
    // send the kid back to the join screen so they can re-pair.
    let body: any = null;
    try {
      body = await response.clone().json();
    } catch {
      // no JSON body; fall through
    }
    if (body?.error === 'device_unpaired') {
      const studentId = getActiveStudentId();
      if (studentId) removePairedStudent(studentId);
      window.location.href = '/join';
      throw new Error('This device is no longer paired. Please sign in again.');
    }

    // Session expired or invalid parent JWT
    if (onUnauthorized) {
      onUnauthorized();
      throw new Error('Your session has expired. Please log in again.');
    }
  }

  let payload: any;
  try {
    payload = await response.json();
  } catch (error) {
    // ignore parse failure, we will fall back to default message
  }

  let message = defaultMessage || 'Request failed';

  if (payload?.detail) {
    if (Array.isArray(payload.detail)) {
      message = payload.detail.map((item: any) => item.msg || item.detail || JSON.stringify(item)).join(' ');
    } else if (typeof payload.detail === 'string') {
      message = payload.detail;
    } else if (typeof payload.detail === 'object') {
      message = payload.detail.msg || JSON.stringify(payload.detail);
    }
  }

  throw new Error(message);
}

/**
 * Centralized API request helper that automatically adds auth headers
 */
export async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {},
  errorMessage: string = 'Request failed'
): Promise<T> {
  const { headers, ...restOptions } = options;

  const finalHeaders = {
    ...getAuthHeaders(),
    ...(headers || {}),
  };

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...restOptions,
    headers: finalHeaders,
  });

  return handleResponse<T>(response, errorMessage);
}

// Generic API Client for features using apiClient.get/post/put/patch/delete
export const apiClient = {
  async get<T>(url: string, options?: { params?: Record<string, any> }): Promise<T> {
    const queryString = options?.params
      ? '?' + new URLSearchParams(
          Object.entries(options.params)
            .filter(([_, v]) => v !== undefined && v !== null)
            .map(([k, v]) => [k, String(v)])
        ).toString()
      : '';

    const response = await fetch(`${API_BASE_URL}${url}${queryString}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    return handleResponse<T>(response, 'GET request failed');
  },

  async post<T>(url: string, data?: any): Promise<T> {
    const response = await fetch(`${API_BASE_URL}${url}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: data ? JSON.stringify(data) : undefined,
    });

    return handleResponse<T>(response, 'POST request failed');
  },

  async put<T>(url: string, data?: any): Promise<T> {
    const response = await fetch(`${API_BASE_URL}${url}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: data ? JSON.stringify(data) : undefined,
    });

    return handleResponse<T>(response, 'PUT request failed');
  },

  async patch<T>(url: string, data?: any): Promise<T> {
    const response = await fetch(`${API_BASE_URL}${url}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: data ? JSON.stringify(data) : undefined,
    });

    return handleResponse<T>(response, 'PATCH request failed');
  },

  async delete<T = void>(url: string, options?: { params?: Record<string, any> }): Promise<T> {
    const queryString = options?.params
      ? '?' + new URLSearchParams(
          Object.entries(options.params)
            .filter(([_, v]) => v !== undefined && v !== null)
            .map(([k, v]) => [k, String(v)])
        ).toString()
      : '';

    const response = await fetch(`${API_BASE_URL}${url}${queryString}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    return handleResponse<T>(response, 'DELETE request failed');
  },
};

// ============================================================================
// Re-exports from feature API modules for backward compatibility.
// New code should import directly from the feature module.
// ============================================================================

export { authApi } from '@/features/auth/api/authApi';
export type { AuthUserProfile, AuthenticatedUser, LoginResponse, RegisterRequest, InviteValidateResponse } from '@/features/auth/api/authApi';

export { storiesApi, transformStory } from '@/features/story/api/storyApi';
export type { Story, Question, GenerateStoryRequest, StoryGenerationJobResponse, JobStatus } from '@/features/story/api/storyApi';

export { themesApi } from '@/features/story/api/themesApi';
export type { ThemeCategory, Theme } from '@/features/story/api/themesApi';

export { sessionsApi } from '@/features/quiz/api/sessionsApi';
export type { Session, SessionSubmission, Answer, SessionResult, QuestionFeedback, SessionDetail } from '@/features/quiz/api/sessionsApi';

export { analyticsApi } from '@/features/analytics/api/analyticsApi';
export type { OverallStats, InferenceAccuracy, RecentPerformance, AnalyticsData, LeaderboardEntry, LeaderboardResponse } from '@/features/analytics/api/analyticsApi';

export { assignmentsApi } from '@/features/student/api/assignmentsApi';
export type { AssignableType, AssignmentSource, AssignmentStatus, Assignment, AssignmentWithDetails, AssignmentCreate, AssignmentUpdate } from '@/features/student/api/assignmentsApi';

export { recommendationsApi } from '@/features/recommendations/api/recommendationsApi';
export type { ScoreBreakdown, RecommendedTheme, RecommendedStory, GenerationRequirement, RecommendationResponse, QuickRecommendationResponse, ThemeRecommendationResponse } from '@/features/recommendations/api/recommendationsApi';

export { meApi, studentsApi, invitesApi, legacyParentApi as parentApi } from '@/features/parent/api/meApi';
export type { StudentProfile, StudentProfileCreate, StudentSettingsUpdate, ChildProgress, ChildActivityItem, ActivitySessionDetail, ActivityDetailResponse, ChildDeckItem, InviteCreate, Invite, AcceptInviteRequest } from '@/features/parent/api/meApi';

export { activitiesApi } from '@/features/story/api/activitiesApi';
export type { ActivityLog } from '@/features/story/api/activitiesApi';

export { jobsApi } from '@/features/story/api/jobsApi';

export { learningProfileApi } from '@/features/student/api/learningProfileApi';
export type { SkillProficiency, LearningProfile, SkillsDueResponse, ParentOverridesUpdate } from '@/features/student/api/learningProfileApi';

